import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './Auth';

function isViewPermission(actionKey: string): boolean {
  return actionKey.includes('view_');
}

export interface PermissionsContextType {
  /** Grupo de permissions: { "products": ["view_products", "edit_product"], ... } */
  permissions: Record<string, string[]>;
  /** Verifica se o usuário tem uma permissão específica (procura em todos os grupos) */
  hasPermission: (actionKey: string) => boolean;
  /** Verifica se o usuário tem ALGUMA das permissões fornecidas */
  hasAnyPermission: (actionKeys: string[]) => boolean;
  /** Checar se o usuário tem TODAS as permissões fornecidas */
  hasAllPermissions: (actionKeys: string[]) => boolean;
  /** Se o usuário é o dono principal da conta (acesso total) */
  isPrimary: boolean;
  /** Loja atual em modo somente leitura (is_blocked sem login_blocked) */
  isStoreReadOnly: boolean;
  /** Se os dados de permissão foram carregados */
  isLoaded: boolean;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  permissions: {},
  hasPermission: () => true,
  hasAnyPermission: () => true,
  hasAllPermissions: () => true,
  isPrimary: false,
  isStoreReadOnly: false,
  isLoaded: false,
});

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { authData } = useAuth();

  // Account auth sem membership: fail-closed. Legacy User / ausência de subject: treat as owner.
  const isPrimary =
    authData?.auth_subject === 'account'
      ? Boolean(authData?.membership?.is_primary)
      : (authData?.membership?.is_primary ?? true);

  const isStoreReadOnly = Boolean(authData?.read_only);

  // Parse permissions do /me: ["products.view_products", "metrics.view_sale_data", ...]
  const permissions: Record<string, string[]> = useMemo(() => {
    if (!authData?.permissions?.length) return {};
    const map: Record<string, string[]> = {};
    for (const key of authData.permissions) {
      const dotIdx = key.indexOf('.');
      const group = dotIdx > 0 ? key.substring(0, dotIdx) : 'general';
      const action = dotIdx > 0 ? key.substring(dotIdx + 1) : key;
      if (!map[group]) map[group] = [];
      map[group].push(action);
    }
    return map;
  }, [authData]);

  const allActionKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const [group, actions] of Object.entries(permissions)) {
      for (const action of actions) {
        keys.add(action); // plain key: "view_products"
        keys.add(`${group}.${action}`); // full key: "products.view_products"
      }
    }
    return keys;
  }, [permissions]);

  const hasPermission = (actionKey: string): boolean => {
    if (isStoreReadOnly && !isViewPermission(actionKey)) return false;
    if (isPrimary) return true;
    return allActionKeys.has(actionKey);
  };

  const hasAnyPermission = (actionKeys: string[]): boolean => {
    if (isStoreReadOnly) {
      return actionKeys.some(
        (key) => isViewPermission(key) && (isPrimary || allActionKeys.has(key)),
      );
    }
    if (isPrimary) return true;
    return actionKeys.some((key) => allActionKeys.has(key));
  };

  const hasAllPermissions = (actionKeys: string[]): boolean => {
    if (isStoreReadOnly) {
      return actionKeys.every(
        (key) => isViewPermission(key) && (isPrimary || allActionKeys.has(key)),
      );
    }
    if (isPrimary) return true;
    return actionKeys.every((key) => allActionKeys.has(key));
  };

  const isLoaded = authData !== undefined;

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isPrimary,
        isStoreReadOnly,
        isLoaded,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextType {
  return useContext(PermissionsContext);
}
